/**
 * Popover + Menu — an anchored panel that reveals from its trigger (scale + fade,
 * design-system.md §5 popover elevation, §12 motion). The panel positions itself with
 * `useFloating` (./use-floating.ts): portaled into `document.body`, `position: fixed`,
 * flipped to the opposite side and clamped inside the viewport by `positionFloating`
 * (./floating.ts) — PO ruling 2026-10-05, "border aware tooltips also": no ancestor
 * `.argus-card`'s `overflow: hidden` clips it, and it never runs off the viewport edge.
 * Closes on Escape and on an outside click (checked against BOTH the trigger wrapper and
 * the portaled panel — the panel is no longer a DOM descendant of the wrapper); focus
 * moves into the panel on open and back to the trigger on close.
 *
 * `Menu` is a Popover whose panel is `role="menu"`, with `MenuItem` buttons and
 * Up/Down/Home/End roving focus.
 */
import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import type { FloatingPlacement } from './floating.ts';
import { useFloating } from './use-floating.ts';

export interface PopoverProps {
  /** The trigger element; it receives `onClick`, `aria-expanded`, `aria-controls`, `aria-haspopup`. */
  trigger: ReactElement;
  children: ReactNode;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  align?: 'start' | 'end' | undefined;
  role?: 'dialog' | 'menu' | undefined;
  'aria-label'?: string | undefined;
  className?: string | undefined;
}

export function Popover({
  trigger,
  children,
  open: controlledOpen,
  onOpenChange,
  align = 'start',
  role = 'dialog',
  className,
  ...rest
}: PopoverProps): JSX.Element {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange],
  );

  const id = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const requestedPlacement: FloatingPlacement = align === 'end' ? 'bottom-end' : 'bottom-start';
  const {
    panelRef,
    style: floatingStyle,
    placement,
  } = useFloating<HTMLDivElement>(wrapRef, open, requestedPlacement);

  // Focus in on open, back to the trigger on close; Escape + outside click close. The
  // outside-click check must look at the panel too: it portals to `document.body`, so it
  // is no longer a DOM descendant of `wrap`.
  useEffect(() => {
    if (!open) return;
    const wrap = wrapRef.current;
    const panel = panelRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const first = panel?.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    (first ?? panel)?.focus();

    function onKey(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
      }
    }
    function onPointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      if (wrap?.contains(target) || panel?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
      if (
        wrap?.contains(document.activeElement) ||
        panel?.contains(document.activeElement) ||
        document.activeElement === document.body
      ) {
        previouslyFocused?.focus?.();
      }
    };
    // `panelRef` comes back from `useFloating`, not a local `useRef` — eslint's ref
    // exemption only recognizes the latter, so it is listed even though (like `wrapRef`)
    // its identity is stable for the component's lifetime.
  }, [open, setOpen, panelRef]);

  const triggerEl = cloneElement(trigger, {
    onClick: (event: MouseEvent) => {
      (trigger.props as { onClick?: (e: MouseEvent) => void }).onClick?.(event);
      setOpen(!open);
    },
    'aria-expanded': open,
    'aria-controls': open ? id : undefined,
    'aria-haspopup': role === 'menu' ? 'menu' : 'dialog',
  } as Record<string, unknown>);

  return (
    <div ref={wrapRef} className="argus-popover">
      {triggerEl}
      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={id}
              role={role}
              aria-label={rest['aria-label']}
              tabIndex={-1}
              data-placement={placement}
              style={floatingStyle}
              className={['argus-floating', 'argus-popover__panel', className ?? '']
                .filter(Boolean)
                .join(' ')}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/* ── Menu ─────────────────────────────────────────────────────────────── */

export interface MenuProps extends Omit<PopoverProps, 'role' | 'children'> {
  children: ReactNode;
}

// A `role="switch"` row (the Switch primitive) joins the roving focus order.
const ITEM_SELECTOR = '[role="menuitem"]:not([disabled]), [role="switch"]:not([disabled])';

export function Menu({ children, ...props }: MenuProps): JSX.Element {
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (event.key === 'ArrowDown') next = (index + 1) % items.length;
    else if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    if (next < 0) return;
    event.preventDefault();
    items[next]?.focus();
  }
  return (
    <Popover role="menu" className="argus-popover__panel--menu" {...props}>
      <div className="argus-menu" onKeyDown={onKeyDown}>
        {children}
      </div>
    </Popover>
  );
}

export interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode | undefined;
  /** Red text for destructive actions. */
  danger?: boolean | undefined;
}

export function MenuItem({
  icon,
  danger = false,
  className,
  children,
  type,
  ...rest
}: MenuItemProps): JSX.Element {
  return (
    <button
      role="menuitem"
      type={type ?? 'button'}
      className={['argus-menu__item', danger ? 'argus-menu__item--danger' : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

export function MenuSeparator(): JSX.Element {
  return <div role="separator" className="argus-menu__separator" />;
}
